import {
	ClientEntityType,
	GuarantorEntityType,
	JobDocumentEntityType,
	JobEntityType,
	JobItemEntityType,
	OrganizationEntityType,
	PatientEntityType,
	PractitionerEntityType,
} from "@/lib/supabase/supabaseTypes";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import { QRCode } from "@progress/kendo-react-barcodes";
import "../fonts.css";
import { PdfJobItemsTable } from "../pdf-template-components/job-items-table/pdf-job-items-table";
import { getClientNameWithCode } from "@/lib/utils/client-utils";
import {
	getPatientLine,
	getPractitionerLine,
} from "../pdf-template-components/pdf-job-information-utils";
import { getGuarantorDisplayName } from "@/dentlab/src/lib/utils/guarantor-utils";
import { PdfTemplateType } from "../document-template.types";
import { useCurrencyHierarchy } from "../../lib/utils/region-utils";
import { PdfFormFields } from "../pdf-template-components/pdf-form-fields.component";
import { PdfOdontogram } from "../pdf-template-components/pdf-odontogram.component";
import { invCoveredByOptions } from "@/dentlab/src/store/Jobs/utils";
import { format } from "date-fns";

interface FormSectionDef {
	id: string;
	title: string;
	description?: string;
	fields: Array<{
		id: string;
		type: string;
		label: string;
		options?: string[];
		odontogramFields?: Array<{
			id: string;
			type: string;
			label: string;
			options?: string[];
		}>;
		appointmentFields?: Array<{
			id: string;
			type: string;
			label: string;
			options?: string[];
		}>;
	}>;
}

interface FormFieldValue {
	fieldType: string;
	fieldValue: any;
}

interface ToothData {
	toothNumber: number;
	values: Record<string, string>;
}

export interface AuftragsblattTemplateProps {
	client: ClientEntityType | null;
	job: JobEntityType | null;
	organization: OrganizationEntityType | null;
	pdfTemplateConfiguration: PdfTemplateConfiguration | undefined;
	patient?: PatientEntityType;
	practitioner?: PractitionerEntityType;
	guarantor?: GuarantorEntityType;
	qrCodeUrl: string;
	formSections?: FormSectionDef[];
	formData?: Record<string, FormFieldValue>;
	formReferences?: Record<string, string>;
	formTitle?: string;
	odontogramData?: ToothData[];
	odontogramFields?: Array<{
		id: string;
		type: string;
		label: string;
		options?: string[];
	}>;
	deliveryNoteItems?: JobItemEntityType[];
	deliveryNoteDocument?: JobDocumentEntityType;
	fileThumbnails?: Record<string, string>;
	excludedFieldIds?: string[];
	jobFiles?: Array<{ name: string; url?: string; type: string }>;
	// Legacy props for backward compat with test print
	jobDocument?: JobDocumentEntityType | null;
	jobItems?: JobItemEntityType[] | null;
	clientAvatarUrl?: string;
}

function formatDate(dateStr?: string | null): string {
	if (!dateStr) return "—";
	try {
		return format(new Date(dateStr), "dd.MM.yyyy");
	} catch {
		return dateStr;
	}
}

function formatTime(timeStr: string): string {
	const parts = timeStr.split(":");
	if (parts.length >= 2) return `${parts[0]}:${parts[1]}`;
	return timeStr;
}

function dateToGermanWeekday(dateStr: string): string {
	const date = new Date(dateStr);
	const weekday = format(date, "EEEE");
	switch (weekday) {
		case "Monday":
			return "MO";
		case "Tuesday":
			return "DI";
		case "Wednesday":
			return "MI";
		case "Thursday":
			return "DO";
		case "Friday":
			return "FR";
		case "Saturday":
			return "SA";
		case "Sunday":
			return "SO";
		default:
			return weekday;
	}
}

export function formatFullDateAndTimeLine(
	dateStr: string,
	timeStr?: string | null
): string {
	const prefix = dateStr ? `${dateToGermanWeekday(dateStr)} ` : "";
	const datePart = formatDate(dateStr);
	const timePart = timeStr ? ` ${formatTime(timeStr)}` : "";
	return `${prefix}${datePart}${timePart}`;
}

export const fullDateAndTime = (
	dateStr: string,
	timeStr: string
): React.ReactNode => {
	return (
		<div
			style={{
				fontSize: "10pt",
				fontWeight: "semibold",
				color: "#000000",
			}}
		>
			{formatFullDateAndTimeLine(dateStr, timeStr)}
		</div>
	);
};

export const AuftragsblattTemplate: React.FC<AuftragsblattTemplateProps> = ({
	client,
	job,
	organization,
	pdfTemplateConfiguration,
	patient,
	practitioner,
	guarantor,
	qrCodeUrl,
	formSections,
	formData,
	formReferences,
	formTitle,
	odontogramData,
	odontogramFields,
	deliveryNoteItems,
	deliveryNoteDocument,
	fileThumbnails,
	excludedFieldIds,
	jobFiles,
	jobDocument,
	jobItems,
}) => {
	const currency = useCurrencyHierarchy(job, client);

	if (!organization || !pdfTemplateConfiguration || !job || !client) {
		return null;
	}

	const hasFormData =
		formSections && formData && Object.keys(formData).length > 0;
	const hasDeliveryItems =
		deliveryNoteItems &&
		deliveryNoteItems.length > 0 &&
		deliveryNoteDocument;
	const hasOdontogramFields = odontogramFields && odontogramFields.length > 0;
	const hasOdontogramData = odontogramData && odontogramData.length > 0;

	const invCoveredByLabel = job.inv_covered_by
		? invCoveredByOptions.find((o) => o.value === job.inv_covered_by)
				?.label ?? job.inv_covered_by
		: null;

	const insuranceText = [invCoveredByLabel, job.insurance]
		.filter(Boolean)
		.join(", ");

	const labelStyle: React.CSSProperties = {
		fontWeight: "semibold",
		color: "#000000",
	};

	const rowStyle: React.CSSProperties = {
		fontSize: "7pt",
		lineHeight: "1.5",
		marginBottom: "0.5mm",
	};

	const sectionLabel: React.CSSProperties = {
		fontSize: "7pt",
		fontWeight: "bold",
		color: "#000000",
		marginBottom: "1mm",
		textTransform: "uppercase" as const,
		letterSpacing: "0.3pt",
	};

	const blockSeparator: React.CSSProperties = {
		borderTop: "0.5pt solid #000000",
		paddingTop: "2mm",
		marginBottom: "3mm",
	};

	const itemsTableConfig = {
		...pdfTemplateConfiguration,
		general: {
			...pdfTemplateConfiguration.general,
			size: "sm" as const,
			tableDensity: "sm" as const,
			tableBackgroundColor: "transparent",
		},
	};

	const isMwstEust =
		job.is_mwst_eust ??
		client.is_mwst_eust ??
		organization.is_mwst_eust ??
		false;

	return (
		<div
			style={{
				fontFamily: "LiberationSans",
				fontWeight: 400,
				fontSize: "7pt",
				lineHeight: "1.5",
				width: "148mm",
				minHeight: "210mm",
				padding: "5mm",
				paddingBottom: "12mm",
				position: "relative",
				color: "#000000",
			}}
		>
			{/* ── HEADER ── */}
			<div
				style={{
					display: "flex",
					alignItems: "flex-start",
					marginBottom: "3mm",
					paddingBottom: "2mm",
					borderBottom: "0.5pt solid #000000",
				}}
			>
				<div style={{ flexShrink: 0, marginRight: "3mm" }}>
					<QRCode
						value={qrCodeUrl}
						errorCorrection="M"
						encoding="UTF_8"
						padding={0}
						style={{ width: "28mm", height: "28mm" }}
					/>
				</div>
				<div style={{ flex: 1 }}>
					<div
						style={{
							fontSize: "13pt",
							fontWeight: "bold",
							color: "#000000",
						}}
					>
						{job.code}
					</div>
					{job.title && (
						<div
							style={{
								fontSize: "10pt",
								fontWeight: "bold",
								marginBottom: "1mm",
							}}
						>
							{job.title}
						</div>
					)}
					<div
						style={{
							...rowStyle,
							fontWeight: "bold",
							fontSize: "9pt",
						}}
					>
						{getClientNameWithCode(client)}
						{practitioner && (
							<>
								{" - "}
								{getPractitionerLine(practitioner)}
							</>
						)}
					</div>
					{guarantor && (
						<div
							style={{
								...rowStyle,
								fontWeight: "bold",
								fontSize: "8pt",
							}}
						>
							<span style={labelStyle}>Garant:</span>{" "}
							{getGuarantorDisplayName(guarantor)}
						</div>
					)}
				</div>
				<div
					style={{
						flexShrink: 0,
						textAlign: "right",
						lineHeight: "1.3",
						marginTop: "1mm",
					}}
				>
					<div
						style={{
							fontSize: "10pt",
							fontWeight: "bold",
							color: "#000000",
							textTransform: "uppercase",
							letterSpacing: "0.3pt",
						}}
					>
						Eingang
					</div>
					{fullDateAndTime(
						job.incoming_order_date ?? job.created_at ?? "",
						job.incoming_order_time ?? ""
					)}
					{job.outgoing_order_date && (
						<>
							<div
								style={{
									fontSize: "10pt",
									fontWeight: "bold",
									color: "#000000",
									textTransform: "uppercase",
									letterSpacing: "0.3pt",
									marginTop: "1mm",
								}}
							>
								Lieferung
							</div>
							{fullDateAndTime(
								job.outgoing_order_date ?? "",
								job.outgoing_order_time ?? ""
							)}
						</>
					)}
				</div>
			</div>

			{/* ── PATIENT + AUFTRAGSDETAILS — two columns ── */}
			<div style={{ marginBottom: "3mm", display: "flex" }}>
				{patient && (
					<div style={{ flex: 1, paddingRight: "3mm" }}>
						<div style={sectionLabel}>Patient</div>
						<div
							style={{
								...rowStyle,
								fontWeight: "bold",
								fontSize: "8pt",
							}}
						>
							{patient.code ? `${patient.code}: ` : ""}
							{
								getPatientLine(patient, client) //print patient code only if client setting is respected
							}
						</div>
						<div style={rowStyle}>
							<span style={labelStyle}>Geb.:</span>{" "}
							{patient.birth_date
								? formatDate(patient.birth_date)
								: "—"}
						</div>
						<div style={rowStyle}>
							<span style={labelStyle}>Versicherung:</span>{" "}
							{job.insurance || "—"}
						</div>
						<div style={rowStyle}>
							<span style={labelStyle}>Kostenübernahme:</span>{" "}
							{invCoveredByLabel || "—"}
						</div>
						{job.is_paid_by_patient && (
							<div
								style={{
									...rowStyle,
									fontWeight: "bold",
								}}
							>
								Patient zahlt Einzelrechnung
							</div>
						)}
					</div>
				)}
				{patient && (
					<div
						style={{
							width: "0.5pt",
							background: "#000000",
							flexShrink: 0,
						}}
					/>
				)}
				<div
					style={{
						flex: 1,
						paddingLeft: patient ? "3mm" : undefined,
					}}
				>
					<div style={sectionLabel}>Auftragsdetails</div>
					<div style={rowStyle}>
						<span style={labelStyle}>Zahnfarbe:</span>{" "}
						{job.tooth_color || "—"}
					</div>
					<div style={{ ...rowStyle, whiteSpace: "pre-wrap" }}>
						<span style={labelStyle}>Beschreibung:</span>{" "}
						{job.description || "—"}
					</div>
				</div>
			</div>

			{/* ── ITEMS TABLE ── */}
			{hasDeliveryItems && (
				<div style={blockSeparator}>
					<div style={sectionLabel}>Positionen</div>
					<PdfJobItemsTable
						jobDocument={deliveryNoteDocument!}
						jobItems={deliveryNoteItems!}
						organization={organization}
						showDrTax={false}
						excludePrices={true}
						pdfTemplateConfiguration={itemsTableConfig}
						isMwstEust={isMwstEust}
						templateType={PdfTemplateType.AUFTRAGSBLATT}
						currency={currency}
					/>
				</div>
			)}
			{!hasDeliveryItems &&
				jobItems &&
				jobItems.length > 0 &&
				jobDocument && (
					<div style={blockSeparator}>
						<PdfJobItemsTable
							jobDocument={jobDocument}
							jobItems={jobItems}
							organization={organization}
							showDrTax={false}
							excludePrices={true}
							pdfTemplateConfiguration={itemsTableConfig}
							isMwstEust={isMwstEust}
							templateType={PdfTemplateType.AUFTRAGSBLATT}
							currency={currency}
						/>
					</div>
				)}

			{/* ── TWO-COLUMN: Form Data | Odontogram ── */}
			<div style={{ ...blockSeparator, display: "flex" }}>
				<div
					style={{
						flex: "50%",
						minWidth: 0,
						paddingRight: "3mm",
					}}
				>
					{hasFormData ? (
						<>
							<div style={sectionLabel}>
								Anfrage
								{formTitle ? `: ${formTitle}` : ""}
							</div>
							<PdfFormFields
								sections={formSections!}
								formData={formData!}
								formReferences={formReferences ?? {}}
								fileThumbnails={fileThumbnails}
								excludedFieldIds={excludedFieldIds}
							/>
						</>
					) : (
						<div
							style={{
								fontSize: "7pt",
								color: "#000000",
								fontStyle: "italic",
							}}
						>
							Keine Anfrage vorhanden
						</div>
					)}
				</div>
				<div
					style={{
						width: "0.5pt",
						background: "#000000",
						flexShrink: 0,
					}}
				/>
				<div
					style={{
						flex: "50%",
						minWidth: 0,
						paddingLeft: "3mm",
					}}
				>
					<PdfOdontogram
						teethData={odontogramData}
						odontogramFields={
							hasOdontogramFields ? odontogramFields : undefined
						}
					/>
				</div>
			</div>

			{/* ── JOB FILES ── */}
			{jobFiles && jobFiles.length > 0 && (
				<div style={blockSeparator}>
					<div style={sectionLabel}>Dateien</div>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: "2mm",
						}}
					>
						{jobFiles.map((file, idx) => {
							const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(
								file.name
							);
							if (isImage && file.url) {
								return (
									<div
										key={idx}
										style={{
											width: "20mm",
											textAlign: "center",
										}}
									>
										<img
											src={file.url}
											style={{
												width: "20mm",
												height: "16mm",
												objectFit: "cover",
												borderRadius: "0.5mm",
												border: "0.3pt solid #000000",
											}}
										/>
										<div
											style={{
												fontSize: "5pt",
												color: "#000000",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{file.name}
										</div>
									</div>
								);
							}
							return (
								<div
									key={idx}
									style={{
										fontSize: "6pt",
										color: "#000000",
										padding: "1mm 2mm",
										background: "#ffffff",
										border: "0.3pt solid #000000",
										borderRadius: "0.5mm",
									}}
								>
									{file.name}
								</div>
							);
						})}
					</div>
				</div>
			)}

			{/* ── FOOTER ── */}
			<div
				style={{
					position: "absolute",
					bottom: "4mm",
					left: "5mm",
					right: "5mm",
					fontSize: "6pt",
					color: "#000000",
					borderTop: "0.3pt solid #000000",
					paddingTop: "1mm",
					display: "flex",
					justifyContent: "space-between",
				}}
			>
				<span>
					Erstellt am {format(new Date(), "dd.MM.yyyy 'um' HH:mm")}
				</span>
				<span>{organization.name} — dentLAB</span>
			</div>
		</div>
	);
};
